import { confirmAlert } from "react-confirm-alert";
import CloseIcon from "@material-ui/icons/Close";
import { Tooltip, TooltipProps } from "@material-ui/core";
import { toast } from "react-toastify";
import AuthService from "../services/auth.service";
import { mapIds, PORT_NAME_PREFIX, YearDaysForCalculation, countType, getPortChargePerMonth } from "./appConstants";
import Button from "../components/DesignSystem/button";
import WarningInfoIcon from "../assets/images/warn-toast-icon.svg";
import DrawerMenu from "react-modern-drawer";
import CloseBtn from "../assets/images/close-drawer.svg";
import { ErrorMessages } from "./apiConstants";
import ErrorInfoIcon from "../assets/images/error-info-icon.svg";
import SuccessInfoIcon from "../assets/images/success-toast-checkmark-icon.svg";
import clsx from "clsx";
import InfoIcon from "../assets/images/info-icon.svg";
import { PortModel } from "@projectstorm/react-diagrams";

export const getTimeInMediumFormat = (date: string | Date) => {
  const dateObj = new Date(date);
  return dateObj.toLocaleString("en-US", { timeStyle: "medium", hour12: false });
};
export const wrapInDrawer = (jsx: any, open: boolean, onClose: () => void, insideDialog?: boolean) => {
  return (
    <DrawerMenu
      enableOverlay
      className={clsx(`manage-ports-drawer`, insideDialog && `inside-dialog`)}
      open={open}
      onClose={() => onClose()}
      duration={100}
      direction="right"
    >
      <div className="close-drawer-btn" onClick={() => onClose()}>
        <img src={CloseBtn} alt="Close Drawer" />
      </div>
      {jsx}
    </DrawerMenu>
  );
};

export const getDateForPriceRow = (date: string | Date) => {
  const dateObj = new Date(date);
  return `${dateObj.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })} ${dateObj.toLocaleString("en-US", {
    timeStyle: "medium",
    hour12: false,
  })}`;
};

export const getDateInMediumFormat = (date: string | Date) => {
  const dateObj = new Date(date);
  return dateObj.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

export const getDateInShortFormat = (date: string | Date) => {
  const dateObj = new Date(date);
  return dateObj.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
  });
};

export const getDateAsMonthYear = (date: string | Date) => {
  const dateObj = new Date(date);
  return dateObj.toLocaleString("en-US", {
    year: "numeric",
    month: "long",
  });
};

export const getDateAsExcelFormat = (date: string | Date): string => {
  if (!date || date === "-") return "";
  const dateObj = new Date(date);
  return `${dateObj.getDate()}-${dateObj.toLocaleString("en-US", {
    month: "short",
  })}-${dateObj.getFullYear()}`;
};

const changeTextForMultipleValues = (unit: string, value: number | string) => {
  if ((typeof value !== "number" ? parseInt(value) : value) > 1) {
    return `${unit}s`;
  }
  return unit;
};

export const wrapInTooltip = (jsx: any, title: any, placement: TooltipProps["placement"] = "right") => {
  return (
    <Tooltip
      classes={{
        tooltip: "tooltip",
      }}
      title={title}
      arrow
      placement={placement}
    >
      {jsx}
    </Tooltip>
  );
};

export const MathNearestTenth = (num: number) => {
  return num;
};

export const elapsedTimeLogic = (date: string | Date): any => {
  const current = new Date().getTime();
  const prev = new Date(date).getTime();
  let difference;
  let differenceUnit;

  const msMin = 60 * 1000; // milliseconds in Minute
  const msHour = msMin * 60; // milliseconds in Hour
  const msDay = msHour * 24; // milliseconds in day
  const msMon = msDay * 30; // milliseconds in Month
  const msYr = msDay * 365; // milliseconds in Year
  const diff = current - prev; // difference between dates.
  if (diff < msHour) {
    difference = Math.round(diff / msMin)
      .toString()
      .padStart(2, "0");
    differenceUnit = changeTextForMultipleValues("Min", difference);
  } else if (diff < msDay) {
    difference = Math.round(diff / msHour)
      .toString()
      .padStart(2, "0");
    differenceUnit = changeTextForMultipleValues("Hour", difference);
  } else if (diff < msMon) {
    difference = Math.round(diff / msDay)
      .toString()
      .padStart(2, "0");
    differenceUnit = changeTextForMultipleValues("Day", difference);
  } else if (diff < msYr) {
    difference = Math.round(diff / msMon)
      .toString()
      .padStart(2, "0");
    differenceUnit = changeTextForMultipleValues("Month", difference);
  } else {
    difference = Math.round(diff / msYr)
      .toString()
      .padStart(2, "0");
    differenceUnit = changeTextForMultipleValues("Year", difference);
  }

  return { difference, differenceUnit };
};

export const getPortName = (portId?: string) => {
  return PORT_NAME_PREFIX.PORT_ID + portId;
};

export const getPortIdFromName = (name: string) => {
  if (name.includes(PORT_NAME_PREFIX.PURCHASE_PORT_ID)) {
    return name.replace(PORT_NAME_PREFIX.PURCHASE_PORT_ID, "");
  }
  return name.replace(PORT_NAME_PREFIX.PORT_ID, "");
};

export const getGroupPortName = (groupId?: string) => {
  return PORT_NAME_PREFIX.GROUP_ID + groupId;
};

export const getAuthHeader = () => {
  const token = AuthService.getToken();
  return { Authorization: token || "" };
};

export const currencyFormatter = (value: number, minimumFractionDigits = 2) => {
  if (!value) "0.00";
  return value.toLocaleString("en-US", { minimumFractionDigits });
};

export const throwGenericToasterError = (errorObject?: { message: string }) => {
  const error = (errorObject && errorObject.message) || ErrorMessages.internalServerError;
  toast.error(error, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
};
export const toasterSuccess = (message: string) => {
  toast.success(message, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
};
export const toasterError = (message: string) => {
  toast.error(message, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
};
export const toastWarning = (message: string) => {
  toast.warn(message, { icon: <img src={WarningInfoIcon} alt="Error" className="error-icon" /> });
};

export const getPricePerMonth = (id: string, price: number) => {
  let result = "";
  if (id === mapIds.contract) {
    result = price ? (price + 2 * getPortChargePerMonth(null)).toFixed(2) : "";
  } else if (id === mapIds.demand) {
    result = price ? ((price / 100) * ((60 * 24 * YearDaysForCalculation) / 12) + 2 * getPortChargePerMonth(null)).toFixed(2) : "";
  }
  return currencyFormatter(parseFloat(result.toString()));
};

export const sumCount = (obj: countType | undefined): number => {
  if (obj) {
    return obj.verified + obj.bought + obj.listed + obj.sold + obj.unverified;
  }
  return 0;
};

const sumAvailable = (obj: countType | undefined): number => {
  if (obj) return obj.verified + obj.bought + obj.listed;
  return 0;
};

export const hasVerified = (obj1: countType, obj2?: countType): boolean => {
  const sum = sumAvailable(obj1) + sumAvailable(obj2);
  return sum > 0;
};

export const hasResources = (obj1: countType, obj2?: countType): boolean => {
  const sum = sumCount(obj1) + sumCount(obj2);
  return sum > 0;
};

export const areYouSureToaster = (onYes: any, port: any, text: string) => {
  return confirmAlert({
    customUI: ({ onClose }) => {
      return (
        <div className="custom-ui-alert">
          <CloseIcon
            className="close-icon-popup"
            onClick={() => {
              onClose();
            }}
          />
          <div>
            <img src={WarningInfoIcon} alt="Warning" />
            <label>{text}</label>
          </div>
          <div />
          <div className="buttons">
            <Button
              onClick={() => {
                onClose();
              }}
              node="No"
              variant="greyed"
              className="cancel"
            />
            <Button
              onClick={() => {
                onYes && onYes(port);
                onClose();
              }}
              node="Yes"
              variant="primary"
            />
          </div>
        </div>
      );
    },
    closeOnEscape: true,
    closeOnClickOutside: true,
  });
};
export const areYouSure = (method: any, text: string) => {
  return confirmAlert({
    customUI: ({ onClose }) => {
      return (
        <div className="custom-ui-alert">
          <CloseIcon
            className="close-icon-popup"
            onClick={() => {
              onClose();
            }}
          />
          <div>
            <img src={WarningInfoIcon} alt="Warning" />
            <label>{text}</label>
          </div>
          <div />
          <div className="buttons">
            <Button
              onClick={() => {
                onClose();
              }}
              node="No"
              variant="greyed"
              className="cancel"
            />
            <Button
              onClick={() => {
                method();
                onClose();
              }}
              node="Yes"
              variant="primary"
            />
          </div>
        </div>
      );
    },
    closeOnEscape: true,
    closeOnClickOutside: true,
  });
};
export enum UserChoice { 
cancelFunction = 1,
dontSaveBeforeApplyingFunction = 2,
saveBeforeApplyingFunction = 3,
}
export const promisedAreYouSure = (text: string): Promise<UserChoice> => {
  return new Promise((resolve) => {
    return confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="custom-ui-alert">
            <CloseIcon
              className="close-icon-popup"
              onClick={() => {
                onClose();
                resolve(UserChoice.cancelFunction);
              }}
            />
            <div>
              <img src={WarningInfoIcon} alt="Warning" />
              <label>{text}</label>
            </div>
            <div />
            <div className="buttons">
              <Button
                onClick={() => {
                  onClose();
                  resolve(UserChoice.dontSaveBeforeApplyingFunction);
                }}
                node="No"
                variant="greyed"
                className="cancel"
              />
              <Button
                onClick={() => {
                  onClose();
                  resolve(UserChoice.saveBeforeApplyingFunction);
                }}
                node="Yes"
                variant="primary"
              />
            </div>
          </div>
        );
      },
      closeOnEscape: true,
      closeOnClickOutside: true,
    });
  });
};
export const areYouSureOrNot = (onYes: () => void, onNo: () => void, text: string) => {
  return confirmAlert({
    customUI: ({ onClose }) => {
      return (
        <div className="custom-ui-alert">
          <CloseIcon
            className="close-icon-popup"
            onClick={() => {
              onNo();
              onClose();
            }}
          />
          <div>
            <img src={WarningInfoIcon} alt="Warning" />
            <label>{text}</label>
          </div>
          <div />
          <div className="buttons">
            <Button
              onClick={() => {
                onNo();
                onClose();
              }}
              node="No"
              variant="greyed"
              className="cancel"
            />
            <Button
              onClick={() => {
                onYes();
                onClose();
              }}
              node="Yes"
              variant="primary"
            />
          </div>
        </div>
      );
    },
    closeOnEscape: true,
    closeOnClickOutside: true,
    onClickOutside() {
      onNo();
    },
    onKeypressEscape() {
      onNo();
    },
  });
};
export const getInfo = () => {
  return <img height="30px" src={InfoIcon} alt="tooltip info" />;
};
export const wrapButtonInTooltip = (title: any, child: any = getInfo(), placement: TooltipProps["placement"] = "left") => {
  return (
    <Tooltip
      classes={{
        tooltip: "tooltip",
      }}
      arrow
      placement={placement}
      title={title}
    >
      <div className="tooltip-button flex left">{child}</div>
    </Tooltip>
  );
};

export const getCityState = (port: any, prefix = ""): string | null => {
  if (port && port[`${prefix}city`] && port[`${prefix}state`]) {
    return `${port[`${prefix}city`]}, ${port[`${prefix}state`]}`;
  }
  return null;
};

export const getMonthStartDate = (): Date => {
  const date = new Date();
  date.setDate(1);
  return date;
};

export const dateWithMonthsDelay = (date: Date, months: number): Date => {
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() + months);
  return newDate;
};

export const getPortOptions = (port: PortModel) => {
  if (port) {
    const options = port.getOptions();
    if (options) {
      if (options.extras) {
        if (options.extras.data) {
          return options.extras.data;
        }
      }
    }
  }
  return null;
};
